import { useCallback, useState } from "react";
import type { LevelDraft } from "@/components/admin/canonical-builder/types";
import { createClientId } from "@/components/admin/canonical-builder/clientId";

type ModuleUpdater = (module: LevelDraft["modules"][number]) => LevelDraft["modules"][number];

export const useCanonicalHierarchyBuilder = () => {
    const [levels, setLevels] = useState<LevelDraft[]>([
        {
            id: createClientId(),
            name: "",
            modules: [],
        },
    ]);

    const updateLevels = useCallback((updater: (draft: LevelDraft[]) => void) => {
        setLevels((prev) => {
            const next = structuredClone(prev);
            updater(next);
            return next;
        });
    }, []);

    const replaceLevels = useCallback((nextLevels: LevelDraft[]) => {
        setLevels(nextLevels.length > 0 ? nextLevels : [{
            id: createClientId(),
            name: "",
            modules: [],
        }]);
    }, []);

    const addLevel = () => {
        updateLevels((draft) => {
            draft.push({
                id: createClientId(),
                name: "",
                modules: [],
            });
        });
    };

    const updateLevelName = (levelId: string, value: string) => {
        updateLevels((draft) => {
            const level = draft.find((item) => item.id === levelId);
            if (level) level.name = value;
        });
    };

    const removeLevel = (levelId: string) => {
        updateLevels((draft) => {
            const index = draft.findIndex((item) => item.id === levelId);
            if (index !== -1) draft.splice(index, 1);
        });
    };

    const addModule = (levelId: string) => {
        updateLevels((draft) => {
            const level = draft.find((item) => item.id === levelId);
            if (!level) return;
            level.modules.push({
                id: createClientId(),
                title: "",
                lessons: [],
            });
        });
    };

    const updateModuleInLevels = (levelId: string, moduleId: string, updater: ModuleUpdater) => {
        updateLevels((draft) => {
            const level = draft.find((item) => item.id === levelId);
            if (!level) return;
            const moduleIndex = level.modules.findIndex((item) => item.id === moduleId);
            if (moduleIndex === -1) return;
            level.modules[moduleIndex] = updater(level.modules[moduleIndex]);
        });
    };

    const updateModuleTitle = (levelId: string, moduleId: string, value: string) => {
        updateModuleInLevels(levelId, moduleId, (module) => ({ ...module, title: value }));
    };

    const removeModule = (levelId: string, moduleId: string) => {
        updateLevels((draft) => {
            const level = draft.find((item) => item.id === levelId);
            if (!level) return;
            const moduleIndex = level.modules.findIndex((item) => item.id === moduleId);
            if (moduleIndex !== -1) level.modules.splice(moduleIndex, 1);
        });
    };

    const addLesson = (levelId: string, moduleId: string) => {
        updateLevels((draft) => {
            const level = draft.find((item) => item.id === levelId);
            if (!level) return;
            const targetModule = level.modules.find((item) => item.id === moduleId);
            if (!targetModule) return;
            targetModule.lessons.push({
                id: createClientId(),
                title: "",
                content: "",
            });
        });
    };

    const removeLesson = (levelId: string, moduleId: string, lessonId: string) => {
        updateModuleInLevels(levelId, moduleId, (module) => ({
            ...module,
            lessons: module.lessons.filter((lesson) => lesson.id !== lessonId),
        }));
    };

    const updateLessonField = (
        levelId: string,
        moduleId: string,
        lessonId: string,
        field: "title" | "content",
        value: string,
    ) => {
        updateModuleInLevels(levelId, moduleId, (module) => ({
            ...module,
            lessons: module.lessons.map((lesson) =>
                lesson.id === lessonId ? { ...lesson, [field]: value } : lesson,
            ),
        }));
    };

    return {
        levels,
        replaceLevels,
        addLevel,
        updateLevelName,
        removeLevel,
        addModule,
        updateModuleTitle,
        removeModule,
        addLesson,
        removeLesson,
        updateLessonField,
    };
};
